import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Cpu, Plug, Save, RefreshCw } from "lucide-react";
import { apiGet, protectedPost } from "@/lib/api";
import { cn } from "@/lib/utils";
import { ModulePage, Label, INPUT, BTN_PRIMARY, BTN_GHOST, NotConnected } from "@/components/common/ModuleShell";

const TIERS = [
  { id: "FAST", desc: "Cheap, low-latency replies — triage, classification, short answers" },
  { id: "STANDARD", desc: "Everyday assistant work — chat, summaries, drafting" },
  { id: "ADVANCED", desc: "Complex reasoning, code, multi-step plans" },
  { id: "PREMIUM", desc: "Highest quality; used only on explicit escalation" },
];
const PROVIDERS = ["openai", "anthropic", "openrouter", "perplexity", "omniroute"];

function OmniRouteCard({ status, onSaved }) {
  const [url, setUrl] = useState(""), [key, setKey] = useState(""), [busy, setBusy] = useState(false), [test, setTest] = useState(null);
  useEffect(() => { setUrl(status?.omniroute_base_url || ""); }, [status]);
  const save = async () => {
    setBusy(true);
    try {
      await protectedPost("/secrets", { omniroute_base_url: url.trim(), ...(key ? { omniroute_key: key } : {}) });
      setKey(""); toast.success("OmniRoute gateway saved."); onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const check = async () => {
    setBusy(true);
    try { const r = await protectedPost("/secrets/test", { provider: "omniroute" }); setTest(r); } catch (e) { setTest({ ok: false, message: e.message }); } finally { setBusy(false); }
  };
  const connected = status?.omniroute === "configured";
  return (
    <div className="jv-panel p-5" data-testid="omniroute-card">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 font-mono text-[12px] uppercase tracking-widest text-jarvis-cyanBright"><Plug className="w-4 h-4" /> OmniRoute gateway</div>
        {connected ? <span className="font-mono text-[10px] uppercase tracking-wider text-jarvis-cyanBright" data-testid="omniroute-status">Configured</span> : <NotConnected testId="omniroute-status">Not connected</NotConnected>}
      </div>
      <p className="text-[12px] text-slate-500 mb-4">Your self-hosted OpenAI-compatible router on the dedicated VPS. Use the HTTPS URL exposed by Caddy/Nginx, never the raw localhost port.</p>
      <Label>Gateway URL</Label>
      <input data-testid="omniroute-url" className={cn(INPUT, "mb-3")} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://omniroute.yourdomain.com" />
      <Label>API key {status?.omniroute_configured_key && <span className="text-jarvis-cyanBright normal-case">· configured (blank keeps current)</span>}</Label>
      <input data-testid="omniroute-key" type="password" className={cn(INPUT, "mb-4")} value={key} onChange={(e) => setKey(e.target.value)} placeholder="omni-…" autoComplete="off" />
      <div className="flex flex-wrap gap-2">
        <button onClick={save} disabled={busy} className={BTN_PRIMARY} data-testid="omniroute-save"><Save className="w-3.5 h-3.5" />Save</button>
        <button onClick={check} disabled={busy || !connected} className={BTN_GHOST} data-testid="omniroute-test"><RefreshCw className={cn("w-3.5 h-3.5", busy && "animate-spin")} />Test connection</button>
      </div>
      {test && <p className={cn("mt-3 font-num text-[11px]", test.ok ? "text-jarvis-cyanBright" : "text-jarvis-red")} data-testid="omniroute-test-result">{test.message}</p>}
    </div>
  );
}

function TierRow({ tier, value, onChange, configured, catalog }) {
  return (
    <div className="grid gap-2 md:grid-cols-[120px_1fr_1fr] items-center py-3 border-b border-white/5 last:border-0" data-testid={`tier-${tier.id}`}>
      <div><div className="font-mono text-xs tracking-[0.2em] text-jarvis-cyanBright">{tier.id}</div><div className="text-[11px] text-slate-500 leading-tight mt-0.5">{tier.desc}</div></div>
      <select data-testid={`tier-${tier.id}-provider`} className={INPUT} value={value.provider || ""} onChange={(e) => onChange({ ...value, provider: e.target.value || null })}>
        <option value="">— unassigned —</option>
        {PROVIDERS.map((p) => <option key={p} value={p}>{p}{configured[p] ? "" : " (no key)"}</option>)}
      </select>
      <div>
        <input data-testid={`tier-${tier.id}-model`} list={value.provider === "omniroute" ? "omniroute-models" : undefined} className={INPUT} value={value.model} onChange={(e) => onChange({ ...value, model: e.target.value })} placeholder="model id" />
      </div>
      {catalog && <datalist id="omniroute-models">{catalog.map((m) => <option key={m} value={m} />)}</datalist>}
    </div>
  );
}

export default function ModelsPage() {
  const [policy, setPolicy] = useState(null), [status, setStatus] = useState(null), [catalog, setCatalog] = useState(null), [busy, setBusy] = useState(false);
  const load = useCallback(() => Promise.all([apiGet("/models/policy"), apiGet("/system/status"), apiGet("/secrets"), apiGet("/models/catalog")])
    .then(([p, s, sec, c]) => { setPolicy(p); setStatus({ ...s, omniroute_configured_key: sec.omniroute_configured }); setCatalog(c); })
    .catch((e) => toast.error(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setBusy(true);
    try { setPolicy(await protectedPost("/models/policy", { tiers: policy.tiers, escalation: policy.escalation })); toast.success("Model policy saved."); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const configured = policy?.configured_providers || {};
  const assigned = policy ? Object.values(policy.tiers).filter((t) => t.provider && t.model).length : 0;
  return (
    <ModulePage icon={Cpu} title="Models" testId="models-page"
      subtitle={policy ? `${assigned}/4 tiers assigned · active provider ${policy.active_provider} · OmniRoute ${status?.omniroute === "configured" ? "configured" : "not connected"}` : "Loading…"}>
      <div className="grid gap-5 xl:grid-cols-[1fr_380px]">
        <div className="jv-panel p-5" data-testid="tier-policy-card">
          <div className="font-mono text-[12px] uppercase tracking-widest text-jarvis-cyanBright mb-1">Tier policy</div>
          <p className="text-[12px] text-slate-500 mb-3">Map each tier to a provider and model. Chat uses the active provider in Settings; tiers drive agent and automation routing.</p>
          {policy && TIERS.map((t) => <TierRow key={t.id} tier={t} value={policy.tiers[t.id]} configured={configured} catalog={catalog?.models?.length ? catalog.models : null}
            onChange={(v) => setPolicy((p) => ({ ...p, tiers: { ...p.tiers, [t.id]: v } }))} />)}
          <label className="mt-4 flex items-center gap-2 font-mono text-[11px] text-slate-300 cursor-pointer">
            <input type="checkbox" data-testid="policy-escalation" checked={!!policy?.escalation} onChange={(e) => setPolicy((p) => ({ ...p, escalation: e.target.checked }))} className="accent-cyan-400" />
            Smart escalation — retry one tier up when a lower tier fails or returns low confidence
          </label>
          <div className="mt-4"><button onClick={save} disabled={busy || !policy} className={BTN_PRIMARY} data-testid="policy-save"><Save className="w-3.5 h-3.5" />Save policy</button></div>
        </div>
        <div className="space-y-5">
          <OmniRouteCard status={status} onSaved={load} />
          <div className="jv-panel p-5" data-testid="omniroute-catalog">
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono text-[12px] uppercase tracking-widest text-jarvis-cyanBright">Gateway catalog</div>
              <span className="font-mono text-[10px] text-slate-500" data-testid="catalog-status">{catalog?.status === "connected" ? `${catalog.models.length} models live` : catalog?.status === "error" ? catalog.message : "not connected"}</span>
            </div>
            {catalog?.models?.length ? <div className="max-h-64 overflow-y-auto space-y-1">{catalog.models.map((m) => <div key={m} className="font-code text-[11px] text-slate-300">{m}</div>)}</div>
              : <p className="text-[12px] text-slate-500">Models are listed live from the gateway once it is configured — nothing is cached or assumed.</p>}
          </div>
        </div>
      </div>
    </ModulePage>
  );
}
